import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Video, Download, Trash2, FileVideo, FileImage, Calendar, Loader2, AlertCircle, HardDrive, Send, Package } from 'lucide-react';
import { isImageFile, isVideoFile, formatFileSize, formatDate, getMediaUrl, getMediaName } from './videoManagerHelpers';

export default function VideoManagerModals({
  userRole,
  previewOpen, setPreviewOpen, selectedItem, handleDownload,
  deleteConfirmOpen, setDeleteConfirmOpen, itemToDelete, handleDelete,
  bulkDeleteOpen, setBulkDeleteOpen, selectedIds, bulkDeleting, handleBulkDelete,
  zipDialogOpen, setZipDialogOpen, zipSubmitting, zipName, setZipName,
  zipMessage, setZipMessage, zipShare, setZipShare, handleCreateZip,
}) {
  return (
    <>
      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              {selectedItem && isImageFile(selectedItem) ? (
                <FileImage className="h-5 w-5 text-pink-600" />
              ) : (
                <Video className="h-5 w-5 text-purple-600" />
              )}
              <span className="truncate">{selectedItem && getMediaName(selectedItem)}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="bg-black flex items-center justify-center min-h-[40vh]">
            {selectedItem && isImageFile(selectedItem) && (
              <img
                src={getMediaUrl(selectedItem)}
                alt={getMediaName(selectedItem)}
                className="max-w-full max-h-[70vh] object-contain"
              />
            )}
            {selectedItem && isVideoFile(selectedItem) && (
              <video
                src={getMediaUrl(selectedItem)}
                controls
                autoPlay
                playsInline
                className="w-full max-h-[70vh]"
              >
                Votre navigateur ne supporte pas la lecture vidéo.
              </video>
            )}
          </div>

          <div className="p-4 border-t bg-gray-50">
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <HardDrive className="h-4 w-4" />
                <span>{formatFileSize(selectedItem?.file_size || selectedItem?.taille)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(selectedItem?.uploaded_at || selectedItem?.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                {selectedItem && isImageFile(selectedItem) ? (
                  <FileImage className="h-4 w-4" />
                ) : (
                  <FileVideo className="h-4 w-4" />
                )}
                <span>{selectedItem?.mime_type || (selectedItem && isImageFile(selectedItem) ? 'image' : 'video')}</span>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => handleDownload(selectedItem)}>
                <Download className="h-4 w-4 mr-2" /> Télécharger
              </Button>
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" /> Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer &quot;{itemToDelete && getMediaName(itemToDelete)}&quot; ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="media-confirm-delete">
              <Trash2 className="h-4 w-4 mr-2" /> Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" /> Suppression en masse
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de supprimer <strong>{selectedIds.size} fichier{selectedIds.size > 1 ? 's' : ''}</strong>. Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="media-bulk-confirm-delete"
            >
              {bulkDeleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Suppression…</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" /> Supprimer {selectedIds.size}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ZIP Dialog */}
      <Dialog open={zipDialogOpen} onOpenChange={(o) => !zipSubmitting && setZipDialogOpen(o)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Package className="h-5 w-5" /> Constituer un dossier ZIP
            </DialogTitle>
            <DialogDescription>
              {selectedIds.size} fichier{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}. Donnez un nom à votre dossier et choisissez son mode de partage.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="zip-name" className="text-sm font-semibold text-slate-700">
                Nom du dossier ZIP
              </Label>
              <Input
                id="zip-name"
                value={zipName}
                onChange={(e) => setZipName(e.target.value)}
                placeholder="Ex: Dossier_chantier_2026-05-19"
                className="mt-1"
                data-testid="zip-name-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Le fichier sera nommé <span className="font-mono">{(zipName || 'dossier_partage').trim()}.zip</span>
              </p>
            </div>

            {userRole === 'member' && (
              <label className="flex items-start gap-3 p-3 rounded-lg border border-purple-200 bg-purple-50/50 cursor-pointer hover:bg-purple-50 transition-colors" data-testid="zip-share-toggle">
                <input
                  type="checkbox"
                  checked={zipShare}
                  onChange={(e) => setZipShare(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded text-purple-600 focus:ring-purple-500"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <Send className="h-3.5 w-3.5 text-purple-600" />
                    Partager directement avec mon COOPER&apos;S
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Le dossier sera envoyé dans la messagerie de votre conseiller référent et téléchargé sur votre appareil.
                  </p>
                </div>
              </label>
            )}

            {zipShare && userRole === 'member' && (
              <div>
                <Label htmlFor="zip-message" className="text-sm font-semibold text-slate-700">
                  Message d&apos;accompagnement (facultatif)
                </Label>
                <Textarea
                  id="zip-message"
                  value={zipMessage}
                  onChange={(e) => setZipMessage(e.target.value)}
                  placeholder="Précisez le contexte (ex: photos chantier rue X, vidéos sinistre du 18/05…)"
                  rows={3}
                  className="mt-1 resize-none"
                  data-testid="zip-message-input"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setZipDialogOpen(false)} disabled={zipSubmitting}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateZip}
              disabled={zipSubmitting || !zipName.trim()}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              data-testid="zip-confirm-btn"
            >
              {zipSubmitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {zipShare ? 'Envoi…' : 'Création…'}</>
              ) : (
                <>{zipShare ? <Send className="h-4 w-4 mr-2" /> : <Download className="h-4 w-4 mr-2" />}{zipShare ? 'Envoyer au COOPER\'S' : 'Télécharger le ZIP'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
